import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { object, bool, func, array, number, string, oneOfType } from 'prop-types';
import { showSlideout, hideSlideout } from 'ps-components/Slideout/SlideoutReducer';
import cn from 'classnames';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import MarketplaceMatches from '../MarketplaceMatches';
import * as marketplaceMatchesActions from '../MarketplaceMatchesActions';
import * as marketPricingActions from '../../MarketPricingActions';
import * as jobPricingActions from '../../JobPricing/JobPricingActions';
import { resetSurveyJobDataCuts } from '../../SurveyJobDataCuts/SurveyJobDataCutActions';
import { jobPricingGridActions } from '../../JobPricingDetails/JobPricingGrid/JobPricingGridCreate';
import { marketplaceMatchesGridActions } from '../MarketplaceMatchesGrid/MarketplaceMatchesGridCreate';
import { getJobPricingDetails } from '../../JobPricing/JobPricingActions';
import Placeholder from '../../Placeholder/Placeholder';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../../../Slideout/Slideouts';
import { isAdHocJobId } from '../../helper';

import './MarketplaceMatchesSlideout.scss';

MPMSlideout.propTypes = {
  actions: object,
  benchmarkYear: number,
  currency: string,
  displayIn: string,
  fieldFormatting: object,
  groupId: oneOfType([number, string]),
  indexMap: object,
  isAdHoc: bool,
  status: string,
  jobCode: oneOfType([number, string]),
  jobCodeKey: oneOfType([number, string]),
  jobId: oneOfType([number, string]),
  jobPricing: object,
  jobPricingActions: object,
  launchDarklyFeatureFlags: object,
  marketPricing: object,
  marketplaceMatches: object,
  masterAccountId: string,
  mpActions: object,
  organizationCode: string,
  readOnlyUser: bool,
  refreshJobTabData: func,
  selectedViewPk: number
};

function MPMSlideout(props) {
  const [renderPopover, setRenderPopover] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState();

  const {
    actions,
    benchmarkYear,
    currency,
    displayIn,
    fieldFormatting,
    groupId,
    indexMap,
    isAdhoc,
    status,
    jobCode,
    jobCodeKey,
    jobId,
    jobPricing,
    jobPricingActions,
    marketPricing,
    marketplaceMatches,
    masterAccountId,
    mpActions,
    organizationCode,
    readOnlyUser,
    refreshJobTabData,
    selectedViewPk,
    launchDarklyFeatureFlags
  } = props;

  const { loading, loaded } = jobPricing.JobPricingGridReducer;

  // == will check both null and undefined
  if (jobId == null) {
    actions.hideSlideout();
  }

  useEffect(() => {
    if (jobId != null) {
      getPricingDetails();
    }
  }, [jobId, jobCode, jobCodeKey, groupId, benchmarkYear, currency, selectedViewPk, displayIn, indexMap]);

  function getPricingDetails() {
    return actions.getJobPricingDetails(
      { code: jobCode, codeKey: jobCodeKey, currency },
      benchmarkYear,
      displayIn,
      jobPricingActions.search,
      masterAccountId
    );
  }

  function getSurveyJobDetailsPageHref(cutId) {
    return `${window.location.origin}/view/o/${organizationCode}/mp/iframe/merit/merit-admin/survey_lib-3?one_cut_id=${cutId}`;
  }

  function togglePopover(id) {
    setRenderPopover(!renderPopover);
    setSelectedRowId(id);
  }

  function updateModifiedDate(jobId, benchmarkYear) {
    if (!isAdHocJobId(jobId)) jobPricingActions.getLastModifiedDate(jobId, benchmarkYear);
  }

  function showSurveyDataCutsSlideout(survey) {
    actions.resetSurveyJobDataCuts();
    actions.showSlideout(MP_SLIDEOUTS.SURVEY_DATA_CUTS, {
      addSelectedCutID: jobPricingActions.addSelectedCutID,
      benchmarkYear,
      currentJob: { id: jobId, jobCode, jobCodeKey },
      displayIn,
      monetaryRounding: fieldFormatting.monetaryRounding,
      onMatchSelect: refreshAfterDatacutChange,
      readOnly: readOnlyUser,
      removeSelectedCutID: jobPricingActions.removeSelectedCutID,
      survey,
      onBack
    });
  }

  function refreshAfterDatacutChange(currency) {
    actions.resetSurveyJobDataCuts();
    refreshJobTabData();
    getPricingDetails();
  }

  async function addMatchFunc(orgJobId, cutId, feedback, isSelectAll, benchmarkYear, slottedInfo) {
    await mpActions.addMatch(orgJobId, cutId, feedback, isSelectAll, benchmarkYear, slottedInfo);
    refreshJobTabData();
    await getPricingDetails();
  }

  async function deleteMatchFunc(orgJobId, cutIds, matchIds, benchmarkYear, isSelectAll, slottedInfo) {
    await mpActions.deleteMatch(orgJobId, cutIds, matchIds, benchmarkYear, isSelectAll, slottedInfo);
    refreshJobTabData();
    await getPricingDetails();
  }

  function onBack() {
    actions.showSlideout(MP_SLIDEOUTS.MARKETPLACE_MATCHES, {
      benchmarkYear,
      currency,
      displayIn,
      groupId,
      indexMap,
      isAdhoc,
      status,
      jobCode,
      jobCodeKey,
      jobId,
      refreshJobTabData,
      selectedViewPk
    });
  }

  return (
    <div className={cn('mpm-slideout', { slotted: status !== 'priced' })} id="mpm-slideout">
      <SlideoutHeader title="Add Marketplace Matches" doClose={actions.hideSlideout} />

      {status !== 'priced' ? (
        <Placeholder
          className="no-matches"
          primaryText="Marketplace recommendations are not available for Slotted jobs"
        />
      ) : (
        <MarketplaceMatches
          actions={actions}
          addMatch={addMatchFunc}
          addSelectedCutID={jobPricingActions.addSelectedCutID}
          benchmarkYear={benchmarkYear}
          currency={currency || 'USD'}
          deleteMatch={deleteMatchFunc}
          displayIn={displayIn}
          getSurveyJobDetailsPageHref={getSurveyJobDetailsPageHref}
          grid={marketplaceMatches.marketplaceMatchesGridReducer}
          groupId={groupId}
          handlePricingChange={refreshJobTabData}
          hideSelect={readOnlyUser}
          isAdHoc={isAdhoc}
          isGetJobPricingLoaded={loaded}
          isGetJobPricingLoading={loading}
          isMatchDeleting={marketPricing.isMatchDeleting}
          jobId={jobId}
          jobPricingGrid={jobPricing.JobPricingGridReducer}
          loadingId={marketPricing.loadingId}
          masterAccountId={masterAccountId}
          onDataCutClick={showSurveyDataCutsSlideout}
          removeSelectedCutID={jobPricingActions.removeSelectedCutID}
          render
          renderPopover={renderPopover}
          selectedRowId={selectedRowId}
          selectedSurveys={jobPricing.MainJobPricingReducer.selectedCutIDs}
          selectedViewPk={selectedViewPk}
          showMinHeader={false}
          toggleHeader={false}
          togglePopover={togglePopover}
          updateModifiedDate={updateModifiedDate}
          viewLoading={false}
          onBack={onBack}
          isSlideout
          launchDarklyFeatureFlags={launchDarklyFeatureFlags}
          {...marketplaceMatches.marketplaceMatchesReducer}
        />
      )}
    </div>
  );
}

const mapStateToProps = ({ session, marketPricing }) => {
  return {
    fieldFormatting: {
      monetaryRounding: session.user.preferences.monetaryRounding,
      numericRounding: session.user.preferences.numericRounding,
      percentRounding: session.user.preferences.percentRounding
    },
    jobPricing: marketPricing.JobPricingReducer,
    marketPricing: marketPricing.MarketPricingReducer,
    marketplaceMatches: marketPricing.MarketplaceMatchesReducer,
    masterAccountId: session.organization.masterAccountId,
    organizationCode: session.organization.code,
    readOnlyUser: session.user.rights.readOnlyUser
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...marketplaceMatchesActions,
      ...marketplaceMatchesGridActions,
      showSlideout,
      hideSlideout,
      getJobPricingDetails,
      resetSurveyJobDataCuts
    },
    dispatch
  ),
  jobPricingActions: bindActionCreators(
    {
      ...jobPricingActions,
      ...jobPricingGridActions
    },
    dispatch
  ),
  mpActions: bindActionCreators(
    {
      ...marketPricingActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(MPMSlideout);
